// Effective price (GLOSSARY.md, ADR 0027): `price` is always the original;
// `sale_price` applies while the half-open window [sale_start_at,
// sale_end_at) contains Shop server clock now. Invalid sale data never omits
// a product — the sale is ignored and reported, and the product sells at
// `price`.

export interface SaleWindow {
  amount: number
  endMs: number
  startMs: number
}

export interface ProductPricing {
  price: number
  sale: SaleWindow | null
}

export interface EffectivePrice {
  amount: number
  // The struck-through original — non-null only while the sale is active.
  original: number | null
}

export type InvalidSaleReason =
  | 'incomplete_window'
  | 'invalid_sale_price'
  | 'inverted_window'
  | 'missing_sale_price'
  | 'sale_price_not_below_price'
  | 'unparseable_window'

// Field types stay unknown so runtime-shaped products (the currency
// hydration's Record<string, unknown>) validate here instead of casting.
interface SaleFields {
  price: number
  sale_price?: unknown
  sale_start_at?: unknown
  sale_end_at?: unknown
}

export function deriveProductPricing(
  product: SaleFields,
  onInvalidSale?: (reason: InvalidSaleReason) => void,
): ProductPricing {
  const { price, sale_price, sale_start_at, sale_end_at } = product
  const noSale: ProductPricing = { price, sale: null }

  const hasSalePrice = sale_price !== undefined && sale_price !== null
  const hasStart = sale_start_at !== undefined && sale_start_at !== null
  const hasEnd = sale_end_at !== undefined && sale_end_at !== null
  // Every sale field absent is the ordinary no-sale product, not an error.
  if (!hasSalePrice && !hasStart && !hasEnd) return noSale

  const invalid = (reason: InvalidSaleReason): ProductPricing => {
    onInvalidSale?.(reason)
    return noSale
  }

  if (!hasSalePrice) return invalid('missing_sale_price')
  if (!hasStart || !hasEnd) return invalid('incomplete_window')
  if (
    typeof sale_price !== 'number' ||
    !Number.isFinite(sale_price) ||
    sale_price <= 0
  ) {
    return invalid('invalid_sale_price')
  }
  if (sale_price >= price) return invalid('sale_price_not_below_price')

  if (typeof sale_start_at !== 'string' || typeof sale_end_at !== 'string') {
    return invalid('unparseable_window')
  }
  const startMs = Date.parse(sale_start_at)
  const endMs = Date.parse(sale_end_at)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return invalid('unparseable_window')
  }
  if (endMs <= startMs) return invalid('inverted_window')

  return { price, sale: { amount: sale_price, endMs, startMs } }
}

export function resolveEffectivePrice(
  pricing: ProductPricing,
  nowMs: number,
): EffectivePrice {
  const { price, sale } = pricing
  if (sale && nowMs >= sale.startMs && nowMs < sale.endMs) {
    return { amount: sale.amount, original: price }
  }
  return { amount: price, original: null }
}

// The Sale tag's percentage (GLOSSARY.md "Sale tag"): the whole-number
// discount off the original, floored so a tag never overstates the saving.
// Dividing the scaled difference, not the ratio, keeps an exact discount
// exact (1 - 550 / 1000 alone lands just under 0.45). Null outside a sale or
// when the discount floors to zero — nothing worth a tag.
export function saleDiscountPercent(effective: EffectivePrice): number | null {
  if (effective.original === null) return null
  const { amount, original } = effective
  const percent = Math.floor(((original - amount) * 100) / original)
  return percent > 0 ? percent : null
}
